import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  BarChart3, Users, TrendingUp, Download, ChevronLeft, ChevronRight,
  Phone, CheckCircle2, Target, FileText, PieChart
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart as RechartsPie, Pie, Cell, AreaChart, Area
} from 'recharts';
import { toast } from 'sonner';
import { MainLayout } from '../components/MainLayout';
import { ChartContainer } from '../components/ChartContainer';
import { generateReportPDF, downloadPDF } from '../utils/pdfExport';

const COLORS = ['#1e3a5f', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

export const ReportsPage = () => {
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [periodType, setPeriodType] = useState('weekly'); // daily, weekly, monthly, yearly
  const [periodOffset, setPeriodOffset] = useState(0);
  const [selectedUser, setSelectedUser] = useState('all');
  const [teamMembers, setTeamMembers] = useState([]);
  const [weeklyData, setWeeklyData] = useState(null);
  const [activityByDay, setActivityByDay] = useState([]);
  const [activityByType, setActivityByType] = useState([]);
  const [userPerformance, setUserPerformance] = useState([]);
  const [enquiryStats, setEnquiryStats] = useState([]);
  const [branding, setBranding] = useState(null);

  // Get date range based on period type
  const getPeriodDates = (type, offset = 0) => {
    const now = new Date();
    let start, end, label;
    
    switch (type) {
      case 'daily':
        start = new Date(now);
        start.setDate(start.getDate() + offset);
        end = new Date(start);
        label = start.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
        break;
        
      case 'weekly':
        start = new Date(now);
        start.setDate(now.getDate() - now.getDay() + 1 + (offset * 7)); // Monday
        end = new Date(start);
        end.setDate(start.getDate() + 6); // Sunday
        label = `${start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
        break;
        
      case 'monthly':
        start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
        end = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
        label = start.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
        break;
        
      case 'yearly':
        start = new Date(now.getFullYear() + offset, 0, 1);
        end = new Date(now.getFullYear() + offset, 11, 31);
        label = start.getFullYear().toString();
        break;
        
      default:
        return getPeriodDates('weekly', offset);
    }
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
      label
    };
  };

  // Legacy function for backward compatibility
  const _getWeekDates = (offset = 0) => getPeriodDates('weekly', offset);

  const loadBranding = useCallback(async () => {
    try {
      const res = await authFetch('/team/branding');
      if (res.ok) {
        const data = await res.json();
        setBranding(data.branding || data);
      }
    } catch (err) {
      console.error('Load branding error:', err);
    }
  }, [authFetch]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const periodDates = getPeriodDates(periodType, periodOffset);
      
      // Load team members
      const membersRes = await authFetch('/admin/team');
      if (membersRes.ok) {
        const data = await membersRes.json();
        setTeamMembers(data.members || []);
      }

      // Load report data with period type
      const reportRes = await authFetch(`/reports/weekly?start=${periodDates.start}&end=${periodDates.end}&user_id=${selectedUser}&period_type=${periodType}`);
      if (reportRes.ok) {
        const data = await reportRes.json();
        setWeeklyData(data);
        setActivityByDay(data.activity_by_day || []);
        setActivityByType(data.activity_by_type || []);
        setUserPerformance(data.user_performance || []);
        setEnquiryStats(data.enquiry_stats || []);
      }
    } catch (err) {
      console.error('Failed to load reports:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authFetch, periodType, periodOffset, selectedUser]);

  useEffect(() => {
    loadData();
    loadBranding();
  }, [loadData, loadBranding]);

  const exportToPDF = async () => {
    try {
      const periodDates = getPeriodDates(periodType, periodOffset);
      
      // Transform activityByDay for the PDF - use totals
      const activityDayMap = {};
      activityByDay.forEach(d => {
        const dayKey = (d.day || '').toLowerCase();
        if (dayKey) {
          activityDayMap[dayKey] = (d.outreach || 0) + (d.tasks || 0) + (d.follow_ups || 0);
        }
      });
      
      const reportData = {
        week_label: periodDates.label,
        enquiries_count: weeklyData?.totals?.enquiries || 0,
        quotes_count: weeklyData?.totals?.quotes || 0,
        won_count: weeklyData?.totals?.won || 0,
        outreach_count: weeklyData?.totals?.outreach || 0,
        pipeline_value: weeklyData?.pipeline_value || 0,
        won_value: weeklyData?.won_value || 0,
        activity_by_day: activityDayMap,
        user_performance: (userPerformance || []).map(u => ({
          name: u.name || 'Unknown',
          enquiries: u.enquiries || 0,
          quotes: u.quotes || 0,
          outreach: u.outreach || 0,
          score: u.score || 0
        }))
      };
      
      const pdfDoc = await generateReportPDF(reportData, branding);
      downloadPDF(pdfDoc, `${periodType.charAt(0).toUpperCase() + periodType.slice(1)}_Report_${periodDates.start}.pdf`);
      toast.success('Report exported to PDF');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Failed to export PDF: ' + (err.message || 'Unknown error'));
    }
  };

  const exportReport = async () => {
    try {
      const periodDates = getPeriodDates(periodType, periodOffset);
      const res = await authFetch(`/reports/export?start=${periodDates.start}&end=${periodDates.end}&user_id=${selectedUser}`);
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${periodType}_report_${periodDates.start}_${periodDates.end}.json`;
        a.click();
        toast.success('Report exported');
      }
    } catch (err) {
      toast.error('Failed to export report');
    }
  };
  
  // Get relative period label
  const getPeriodLabel = () => {
    if (periodOffset === 0) {
      switch (periodType) {
        case 'daily': return 'Today';
        case 'weekly': return 'This Week';
        case 'monthly': return 'This Month';
        case 'yearly': return 'This Year';
        default: return 'Current';
      }
    } else if (periodOffset === -1) {
      switch (periodType) {
        case 'daily': return 'Yesterday';
        case 'weekly': return 'Last Week';
        case 'monthly': return 'Last Month';
        case 'yearly': return 'Last Year';
        default: return 'Previous';
      }
    }
    const unit = periodType === 'daily' ? 'day' : periodType.slice(0, -2);
    return `${Math.abs(periodOffset)} ${unit}${Math.abs(periodOffset) > 1 ? 's' : ''} ${periodOffset < 0 ? 'ago' : 'from now'}`;
  };

  const periodDates = getPeriodDates(periodType, periodOffset);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-slate-500">Loading reports...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 max-w-full overflow-x-hidden" data-testid="reports-page">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="font-heading text-xl md:text-3xl font-bold text-[#1e3a5f]">
              Reports
            </h1>
            <p className="text-slate-500 mt-1 text-sm md:text-base">
              Team performance and activity summary
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            {/* Period Type Selector */}
            <Select value={periodType} onValueChange={(val) => { setPeriodType(val); setPeriodOffset(0); }}>
              <SelectTrigger className="w-full sm:w-32 rounded-xl" data-testid="period-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={selectedUser} onValueChange={setSelectedUser}>
              <SelectTrigger className="w-full sm:w-48 rounded-xl">
                <SelectValue placeholder="All Team" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Team</SelectItem>
                {teamMembers.map(member => (
                  <SelectItem key={member.id} value={member.id}>
                    {member.first_name} {member.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex gap-2">
              <Button
                onClick={exportToPDF}
                className="flex-1 sm:flex-none bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full text-sm"
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4 mr-1 md:mr-2" />
                <span className="hidden sm:inline">Export </span>PDF
              </Button>
              <Button
                onClick={exportReport}
                variant="outline"
                className="flex-1 sm:flex-none rounded-full text-sm"
              >
                <Download className="w-4 h-4 mr-1 md:mr-2" />
                JSON
              </Button>
            </div>
          </div>
        </div>

        {/* Period Navigation */}
        <div className="flex items-center justify-center gap-4 bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPeriodOffset(prev => prev - 1)}
            className="rounded-full"
            data-testid="period-prev-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="text-center min-w-[250px]">
            <p className="font-heading text-lg font-semibold text-[#1e3a5f]">
              {periodDates.label}
            </p>
            <p className="text-sm text-slate-500">
              {getPeriodLabel()}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPeriodOffset(prev => prev + 1)}
            disabled={periodOffset >= 0}
            className="rounded-full"
            data-testid="period-next-btn"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
                <Phone className="w-5 h-5 text-sky-600" />
              </div>
              <span className="text-sm font-medium text-slate-500">Outreach</span>
            </div>
            <p className="font-heading text-3xl font-bold text-[#1e3a5f]">
              {weeklyData?.totals?.outreach || 0}
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-sm font-medium text-slate-500">Tasks Done</span>
            </div>
            <p className="font-heading text-3xl font-bold text-emerald-600">
              {weeklyData?.totals?.tasks_completed || 0}
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-violet-600" />
              </div>
              <span className="text-sm font-medium text-slate-500">Enquiries</span>
            </div>
            <p className="font-heading text-3xl font-bold text-violet-600">
              {weeklyData?.totals?.enquiries || 0}
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Target className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-sm font-medium text-slate-500">Quotes Sent</span>
            </div>
            <p className="font-heading text-3xl font-bold text-amber-600">
              {weeklyData?.totals?.quotes || 0}
            </p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Activity by Day */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6 overflow-hidden">
            <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4 flex items-center gap-2 text-sm md:text-base">
              <BarChart3 className="w-4 h-4 md:w-5 md:h-5" />
              Activity by Day
            </h3>
            {activityByDay.length === 0 || activityByDay.every(d => d.outreach === 0 && d.tasks === 0 && d.follow_ups === 0) ? (
              <div className="h-64 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No activity this week</p>
                </div>
              </div>
            ) : (
              <ChartContainer height={280}>
                <BarChart data={activityByDay} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} interval={0} />
                  <YAxis tick={{ fontSize: 10 }} width={30} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '12px'
                    }} 
                  />
                  <Legend wrapperStyle={{ fontSize: '10px' }} />
                  <Bar dataKey="outreach" name="Outreach" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="tasks" name="Tasks" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="follow_ups" name="Follow-ups" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ChartContainer>
            )}
          </div>

          {/* Activity by Type */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6 overflow-hidden">
            <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4 flex items-center gap-2 text-sm md:text-base">
              <PieChart className="w-4 h-4 md:w-5 md:h-5" />
              Activity Breakdown
            </h3>
            {activityByType.length === 0 || activityByType.every(d => d.value === 0) ? (
              <div className="h-64 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <PieChart className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No activity data</p>
                </div>
              </div>
            ) : (
              <ChartContainer height={280}>
                <RechartsPie>
                  <Pie
                    data={activityByType}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {activityByType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                </RechartsPie>
              </ChartContainer>
            )}
          </div>
        </div>

        {/* User Performance Table */}
        {selectedUser === 'all' && userPerformance.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="font-heading font-semibold text-[#1e3a5f] flex items-center gap-2">
                <Users className="w-5 h-5" />
                Individual Performance
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Team Member</th>
                    <th className="text-center px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Outreach</th>
                    <th className="text-center px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Follow-ups</th>
                    <th className="text-center px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Tasks Done</th>
                    <th className="text-center px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Enquiries</th>
                    <th className="text-center px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Quotes</th>
                    <th className="text-center px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Score</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {userPerformance.map((perf, idx) => (
                    <tr key={perf.user_id || idx} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-[#1e3a5f] rounded-full flex items-center justify-center text-white text-sm font-bold">
                            {perf.name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{perf.name}</p>
                            <p className="text-xs text-slate-500">{perf.role}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-sky-100 text-sky-700">
                          {perf.outreach || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-violet-100 text-violet-700">
                          {perf.follow_ups || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-700">
                          {perf.tasks_completed || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-slate-700">{perf.enquiries || 0}</span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-slate-700">{perf.quotes || 0}</span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                            (perf.score || 0) >= 80 ? 'bg-emerald-500' :
                            (perf.score || 0) >= 60 ? 'bg-amber-500' : 'bg-red-500'
                          }`}>
                            {perf.score || 0}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Enquiry Pipeline Stats */}
        {enquiryStats.length > 0 && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Enquiry Pipeline This Week
            </h3>
            <ChartContainer height={250}>
              <AreaChart data={enquiryStats}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="status" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Area type="monotone" dataKey="count" stroke="#1e3a5f" fill="#1e3a5f" fillOpacity={0.2} />
              </AreaChart>
            </ChartContainer>
          </div>
        )}
      </div>
    </MainLayout>
  );
};
